import React from 'react';
import { usePermissions } from '../hooks/usePermissions';

/**
 * PermissionGate - Conditionally renders children based on user permissions
 *
 * Usage examples:
 *
 * // Require specific role
 * <PermissionGate requireRole="System Administrator">
 *   <AdminPanel />
 * </PermissionGate>
 *
 * // Require any of specified roles
 * <PermissionGate requireAnyRole={["Agent", "System Administrator"]}>
 *   <AgentFeatures />
 * </PermissionGate>
 *
 * // Require specific permission
 * <PermissionGate requirePermission="properties.manage">
 *   <EditButton />
 * </PermissionGate>
 *
 * // Require agent features
 * <PermissionGate requireAgent>
 *   <PropertiesLink />
 * </PermissionGate>
 *
 * // Require admin features
 * <PermissionGate requireAdmin>
 *   <AdminLink />
 * </PermissionGate>
 *
 * // With fallback
 * <PermissionGate requireAdmin fallback={<p>Access denied</p>}>
 *   <AdminPanel />
 * </PermissionGate>
 */
const PermissionGate = ({
  children,
  requireRole,
  requireAnyRole,
  requireAllRoles,
  requirePermission,
  requireAnyPermission,
  requireAllPermissions,
  requireAgent = false,
  requireAdmin = false,
  fallback = null,
}) => {
  const {
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessAgentFeatures,
    canAccessAdminFeatures,
  } = usePermissions();

  // Check all permission requirements
  let hasAccess = true;

  // Role checks
  if (requireRole && !hasRole(requireRole)) {
    hasAccess = false;
  }

  if (requireAnyRole && !hasAnyRole(requireAnyRole)) {
    hasAccess = false;
  }

  if (requireAllRoles && !hasAllRoles(requireAllRoles)) {
    hasAccess = false;
  }

  // Permission checks
  if (requirePermission && !hasPermission(requirePermission)) {
    hasAccess = false;
  }

  if (requireAnyPermission && !hasAnyPermission(requireAnyPermission)) {
    hasAccess = false;
  }

  if (requireAllPermissions && !hasAllPermissions(requireAllPermissions)) {
    hasAccess = false;
  }

  // Convenience checks
  if (requireAgent && !canAccessAgentFeatures) {
    hasAccess = false;
  }

  if (requireAdmin && !canAccessAdminFeatures) {
    hasAccess = false;
  }

  // Render children if user has access, otherwise render fallback
  return hasAccess ? children : fallback;
};

export default PermissionGate;
